import React, { useState, useMemo, useEffect } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';
import type { CatalogCategoryNode } from '../types/catalog'; // Ruta corregida

interface IndustryFilterProps {
    allIndustries: Array<{ name: string }>; // Mantener si se usa para un filtro de industrias separado
    selectedIndustries: string[];
    onIndustryChange: (industry: string) => void;
    
    brands: string[];
    selectedBrands: string[];
    onBrandChange: (brand: string) => void;
    
    selectedCategories: string[]; // Ahora contendrá IDs de categorías/subcategorías
    onCategoryChange: (categoryId: string) => void; // Ahora maneja IDs

    categoryStructure: CatalogCategoryNode[]; // Añadido para recibir la estructura jerárquica
    
    // Filtro de Talles
    sizes?: string[];
    selectedSizes?: string[];
    onSizeChange?: (size: string) => void;

    className?: string;
    isMobile?: boolean;
    initialOpenSections?: string[];
}

// Sección para filtrar por talles con pills modernos
interface SizeFilterSectionProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  sizes: string[];
  selectedSizes: string[];
  onSizeChange: (size: string) => void;
}

const SizeFilterSection: React.FC<SizeFilterSectionProps> = ({
  title,
  isOpen,
  onToggle,
  sizes = [],
  selectedSizes = [],
  onSizeChange
}) => {
  if (sizes.length === 0) return null;

  return (
    <div className="py-2 border-b border-gray-100 last:border-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-sm text-gray-700 hover:text-gray-900"
      >
        <div className="flex items-center">
          <span className="font-medium">{title}</span>
          {selectedSizes.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full font-medium">
              {selectedSizes.length}
            </span>
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="mt-3">
          <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto pr-1">
            {sizes.map((size) => {
              const isSelected = selectedSizes.includes(size);
              return (
                <button
                  key={size}
                  type="button"
                  onClick={() => onSizeChange(size)}
                  className={`px-2.5 py-1 text-xs font-semibold rounded-md border transition-all duration-150 ${
                    isSelected
                      ? 'bg-amber-600 text-white border-amber-600 shadow-sm'
                      : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-amber-50 hover:border-amber-300'
                  }`}
                >
                  {size}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Props para la sección de categorías jerárquicas
interface HierarchicalCategorySectionProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  categoryStructure: CatalogCategoryNode[];
  selectedCategories: string[];
  onCategoryChange: (categoryId: string) => void;
}


interface FilterSectionProps {
    title: string;
    isOpen: boolean;
    onToggle: () => void;
    items: string[];
    selectedItems: string[];
    onItemChange: (item: string) => void;
    searchTerm: string;
    maxItems?: number; // Nueva prop para limitar items visibles
    showSelectAll?: boolean; // Nueva prop para mostrar opción "Todas"
}

const FilterSection: React.FC<FilterSectionProps> = ({
    title,
    isOpen,
    onToggle,
    items = [],
    selectedItems = [],
    onItemChange,
    searchTerm = '',
    maxItems,
    showSelectAll = false // Nueva prop
}) => {
    const filteredItems = useMemo(() => {
        if (!items) return [];
        return items.filter(item =>
            item?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [items, searchTerm]);

    const itemsToShow = maxItems ? filteredItems.slice(0, maxItems) : filteredItems;
    const hasMoreItems = maxItems && filteredItems.length > maxItems;
    
    const allSelected = filteredItems.length > 0 && filteredItems.every(item => selectedItems.includes(item));
    const someSelected = filteredItems.some(item => selectedItems.includes(item));

    const handleSelectAll = () => {
        if (allSelected) {
            // Deseleccionar todas
            filteredItems.forEach(item => {
                if (selectedItems.includes(item)) {
                    onItemChange(item);
                }
            });
        } else {
            // Seleccionar todas
            filteredItems.forEach(item => {
                if (!selectedItems.includes(item)) {
                    onItemChange(item);
                }
            });
        }
    };

    return (
        <div className="py-2 border-b border-gray-100 last:border-0">
            <button
                onClick={onToggle}
                className="w-full flex items-center justify-between text-sm text-gray-700 hover:text-gray-900"
            >
                <div className="flex items-center">
                    <span className="font-medium">{title}</span>
                    {selectedItems.length > 0 && (
                        <span className="ml-2 px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">
                            {selectedItems.length}
                        </span>
                    )}
                </div>
                <ChevronDown
                    className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>
            {isOpen && (
                <div className={`mt-2 ${maxItems && items.length > maxItems ? 'max-h-40 overflow-y-auto custom-scrollbar' : 'max-h-48 overflow-y-auto custom-scrollbar'}`}>
                    {/* Opción "Todas" para industrias */}
                    {showSelectAll && (
                        <label className="flex items-center py-1.5 px-2 hover:bg-gray-50 rounded cursor-pointer text-sm border-b border-gray-100 mb-2">
                            <input
                                type="checkbox"
                                checked={allSelected}
                                ref={input => {
                                    if (input) input.indeterminate = someSelected && !allSelected;
                                }}
                                onChange={handleSelectAll}
                                className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                            />
                            <span className="ml-2 text-gray-700 font-medium">
                                {allSelected ? 'Deseleccionar todas' : 'Seleccionar todas'}
                            </span>
                        </label>
                    )}
                    
                    {itemsToShow.length > 0 ? (
                        itemsToShow.map((item) => (
                            <label
                                key={item}
                                className="flex items-center py-1.5 px-2 hover:bg-gray-50 rounded cursor-pointer text-sm"
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedItems.includes(item)}
                                    onChange={() => onItemChange(item)}
                                    className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                                />
                                <span className="ml-2 text-gray-700">{item}</span>
                            </label>
                        ))
                    ) : (
                        <div className="text-sm text-gray-500 py-2 px-2">
                            No se encontraron resultados
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};


// Nuevo componente para la sección de categorías jerárquicas
const HierarchicalCategoryFilterSection: React.FC<HierarchicalCategorySectionProps> = ({
  title,
  isOpen,
  onToggle,
  categoryStructure,
  selectedCategories, // IDs
  onCategoryChange, // (categoryId: string) => void
}) => {
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

  // Expandir automáticamente las categorías que tienen hijos seleccionados
  useEffect(() => {
    const categoriesToExpand: string[] = [];
    
    const findParentsOfSelected = (nodes: CatalogCategoryNode[], parentId?: string) => {
      nodes.forEach(node => {
        if (selectedCategories.includes(node.id) && parentId) {
          if (!categoriesToExpand.includes(parentId)) {
            categoriesToExpand.push(parentId);
          }
        }
        if (node.children && node.children.length > 0) {
          findParentsOfSelected(node.children, node.id);
        }
      });
    };
    
    findParentsOfSelected(categoryStructure);
    
    if (categoriesToExpand.length > 0) {
      setExpandedCategories(prev => {
        const newExpanded = [...new Set([...prev, ...categoriesToExpand])];
        return newExpanded;
      });
    }
  }, [selectedCategories, categoryStructure]);

  const handleParentCategoryToggle = (parentId: string) => {
    setExpandedCategories(prev =>
      prev.includes(parentId) ? prev.filter(id => id !== parentId) : [...prev, parentId]
    );
  };

  // Función para obtener todos los IDs de un nodo y sus descendientes
  const getAllDescendantIds = (node: CatalogCategoryNode): string[] => {
    const ids = [node.id];
    if (node.children) {
      node.children.forEach(child => {
        ids.push(...getAllDescendantIds(child));
      });
    }
    return ids;
  };

  // Función para verificar si una categoría tiene hijos seleccionados
  const hasSelectedChildren = (node: CatalogCategoryNode): boolean => {
    if (!node.children || node.children.length === 0) return false;
    
    return node.children.some(child => 
      selectedCategories.includes(child.id) || hasSelectedChildren(child)
    );
  };

  // Manejador de cambio con selección en cascada
  const handleCategoryToggle = (node: CatalogCategoryNode) => {
    const isCurrentlySelected = selectedCategories.includes(node.id);
    const allIds = getAllDescendantIds(node);

    if (isCurrentlySelected) {
      // Deseleccionar el nodo y todos sus descendientes
      allIds.forEach(id => {
        if (selectedCategories.includes(id)) {
          onCategoryChange(id);
        }
      });
    } else {
      // Seleccionar el nodo y todos sus descendientes
      allIds.forEach(id => {
        if (!selectedCategories.includes(id)) {
          onCategoryChange(id);
        }
      });
    }
  };

  const renderCategoryNode = (node: CatalogCategoryNode, level: number = 0) => {
    const isSelected = selectedCategories.includes(node.id);
    const isExpanded = expandedCategories.includes(node.id);
    const hasPartialSelection = !isSelected && hasSelectedChildren(node);

    return (
      <div key={node.id} style={{ marginLeft: `${level * 16}px` }} className="py-1">
        <div className="flex items-center justify-between">
          <label className="flex items-center py-1 px-1 hover:bg-gray-50 rounded cursor-pointer text-sm flex-grow">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => handleCategoryToggle(node)}
              className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500 flex-shrink-0"
            />
            <span className={`ml-2 ${hasPartialSelection ? 'text-amber-600 font-medium' : 'text-gray-700'}`}>
              {node.name}
              {hasPartialSelection && (
                <span className="ml-1 text-xs text-amber-600">●</span>
              )}
            </span>
          </label>
          {node.children && node.children.length > 0 && (
            <button onClick={() => handleParentCategoryToggle(node.id)} className="p-1 flex-shrink-0">
              <ChevronDown
                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              />
            </button>
          )}
        </div>
        {isExpanded && node.children && node.children.length > 0 && (
          <div className="mt-1">
            {node.children.map(childNode => renderCategoryNode(childNode, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="py-2 border-b border-gray-100 last:border-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-sm text-gray-700 hover:text-gray-900"
      >
        <div className="flex items-center">
          <span className="font-medium">{title}</span>
          {selectedCategories.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">
              {selectedCategories.length}
            </span>
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
          {categoryStructure.map(node => renderCategoryNode(node))}
        </div>
      )}
    </div>
  );
};


const IndustryFilter: React.FC<IndustryFilterProps> = ({
    allIndustries = [],
    selectedIndustries = [],
    onIndustryChange,
    brands = [],
    selectedBrands = [],
    onBrandChange,
    categoryStructure = [], // Valor por defecto
    selectedCategories = [],
    onCategoryChange,
    sizes = [],
    selectedSizes = [],
    onSizeChange,
    className = '',
    isMobile = false,
    initialOpenSections = ['brands', 'categories'], // Talles replegado por default
}) => {
    const [isIndustryOpen, setIsIndustryOpen] = useState(initialOpenSections.includes('industry'));
    const [isBrandsOpen, setIsBrandsOpen] = useState(initialOpenSections.includes('brands'));
    const [isCategoriesOpen, setIsCategoriesOpen] = useState(initialOpenSections.includes('categories'));
    const [isSizesOpen, setIsSizesOpen] = useState(initialOpenSections.includes('sizes') || selectedSizes.length > 0);

    const clearFilters = () => {
        selectedIndustries.forEach(industry => onIndustryChange(industry));
        selectedBrands.forEach(brand => onBrandChange(brand));
        selectedCategories.forEach(category => onCategoryChange(category));
        if (onSizeChange) {
            selectedSizes.forEach(size => onSizeChange(size));
        }
    };

    const hasActiveFilters = selectedIndustries.length > 0 ||
                           selectedBrands.length > 0 ||
                           selectedCategories.length > 0 ||
                           selectedSizes.length > 0;

    return (
        <div className={`bg-white rounded-xl shadow-lg border border-gray-100 ${
            isMobile ? 'w-full' : 'w-full lg:w-72'
        } ${className}`}>
            <div className="p-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-800">Filtros</h2>
                    {hasActiveFilters && (
                        <button
                            onClick={clearFilters}
                            className="text-sm text-amber-600 hover:text-amber-700 flex items-center"
                        >
                            <X className="w-4 h-4 mr-1" />
                            Restablecer
                        </button>
                    )}
                </div>

                {/* Secciones de Filtros - Reordenadas */}
                <div className="space-y-2">
                    {/* 1. Categorías Jerárquicas */}
                    <HierarchicalCategoryFilterSection
                        title="Categorías"
                        isOpen={isCategoriesOpen}
                        onToggle={() => setIsCategoriesOpen(!isCategoriesOpen)}
                        selectedCategories={selectedCategories}
                        onCategoryChange={onCategoryChange}
                        categoryStructure={categoryStructure}
                    />

                    {/* 2. Filtro de Talles */}
                    {sizes.length > 0 && onSizeChange && (
                        <SizeFilterSection
                            title="Talles"
                            isOpen={isSizesOpen}
                            onToggle={() => setIsSizesOpen(!isSizesOpen)}
                            sizes={sizes}
                            selectedSizes={selectedSizes}
                            onSizeChange={onSizeChange}
                        />
                    )}

                    {/* 3. Marcas */}
                    <FilterSection
                        title="Marcas"
                        isOpen={isBrandsOpen}
                        onToggle={() => setIsBrandsOpen(!isBrandsOpen)}
                        items={brands}
                        selectedItems={selectedBrands}
                        onItemChange={onBrandChange}
                        searchTerm=""
                    />

                    {/* 4. Industrias */}
                    <FilterSection
                        title="Industrias"
                        isOpen={isIndustryOpen}
                        onToggle={() => setIsIndustryOpen(!isIndustryOpen)}
                        items={allIndustries.map(i => i.name)}
                        selectedItems={selectedIndustries}
                        onItemChange={onIndustryChange}
                        searchTerm=""
                        showSelectAll={true} // Mostrar opción "Todas" para industrias
                    />

                    {/* La sección 4 de Categorías Relevantes por Industria se elimina */}
                </div>
            </div>
        </div>
    );
};

// IndustryFilter.defaultProps = {
//     initialOpenSections: ['brands', 'categories'],
// };

export default IndustryFilter;
